import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../utils/api';
import { Loader2 } from 'lucide-react';
import logo from '../assets/logo-horizontal.png';
import ReportView from '../components/ReportView';

export default function SharedReport() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [match, setMatch]   = useState(null);
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);

  useEffect(() => {
    publicApi.getSharedReport(token)
      .then((payload) => {
        const r = payload.report || null;
        setReport(r);
        // API returns match as a top-level field alongside report.
        setMatch(payload.match || r?.match || null);
        setExpiresAt(payload.expiresAt || payload.expires_at || r?.expires_at || null);
      })
      .catch((e) => setError(e.message || 'This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="text-center py-24">
      <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-500" />
    </div>
  );

  if (error || !report) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-navy-900 font-medium mb-2">{error || 'Report not found.'}</p>
          <p className="text-gray-400 text-sm">Ask the sender for a new link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-50">
      {/* Shared by Tactmark header */}
      <div className="bg-navy-900 py-5 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <img src={logo} alt="Tactmark" className="h-9" />
          <div className="text-right">
            <p className="text-navy-300 text-xs font-semibold tracking-widest uppercase">Shared by Tactmark</p>
            {expiresAt && <p className="text-navy-400 text-xs mt-1">Link expires: {new Date(expiresAt).toLocaleDateString()}</p>}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Report title bar */}
        <div className="bg-navy-900 rounded-2xl p-6 mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-navy-300 text-xs font-semibold tracking-widest uppercase mb-2">
              Tactmark Analysis Engine · Shared report
            </p>
            <h1 className="text-2xl font-bold text-white">{report.report_title || 'Post-Match Analysis'}</h1>
          </div>
          <p className="text-navy-300 text-xs">Read-only shared report</p>
        </div>

        {/* Full 12-section report same layout as AnalysisReport */}
        <ReportView
          match={match}
          report={report}
          externalVideoUrl={match ? (match.external_video_url || null) : null}
          showActions={false}
        />

        {/* Powered-by footer */}
        <div className="text-center py-8 border-t border-gray-200">
          <p className="text-navy-700 font-semibold text-sm">Powered by Tactmark Analysis Engine</p>
          <p className="text-gray-400 text-xs mt-1">
            Shared read-only from Tactmark. This link does not require an account.
          </p>
        </div>
      </div>
    </div>
  );
}
